import React, { useState, useRef } from 'react';
import axios from 'axios';
import './PDFUploader.css'; // Reuse the same styles

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const MarkdownUploader = ({ onUploadSuccess }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState(null);
  const [results, setResults] = useState([]);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [cancelling, setCancelling] = useState(false);
  const abortControllerRef = useRef(null);
  const cancelledRef = useRef(false);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length > 0) {
      const validExtensions = ['.md', '.markdown'];
      const validMimeTypes = ['text/markdown', 'text/plain', 'text/x-markdown'];
      
      const validFiles = [];
      const invalidFiles = [];
      
      selectedFiles.forEach(file => {
        const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
        if (validExtensions.includes(fileExtension) || validMimeTypes.includes(file.type)) {
          validFiles.push(file);
        } else {
          invalidFiles.push(file.name);
        }
      });
      
      if (invalidFiles.length > 0) {
        setError(`Invalid files skipped: ${invalidFiles.join(', ')}. Only .md or .markdown files are allowed.`);
      } else {
        setError(null);
      }
      
      if (validFiles.length > 0) {
        setFiles(validFiles);
        setResults([]);
        setStatus('');
        if (!isExpanded) {
          setIsExpanded(true);
        }
      }
    }
  };

  const handleCancelUpload = () => {
    setCancelling(true);
    cancelledRef.current = true;
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      setError('Please select at least one file');
      return;
    }

    setUploading(true);
    setError(null);
    setResults([]);
    setCancelling(false);
    cancelledRef.current = false;
    setProgress({ current: 0, total: files.length });
    
    if (!isExpanded) {
      setIsExpanded(true);
    }

    const uploadResults = [];
    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < files.length; i++) {
      if (cancelledRef.current) {
        setStatus(`Upload cancelled. ${successCount} succeeded, ${errorCount} failed, ${files.length - i} skipped.`);
        break;
      }

      const file = files[i];
      setProgress({ current: i + 1, total: files.length });
      setStatus(`Uploading ${file.name} (${i + 1}/${files.length})...`);

      abortControllerRef.current = new AbortController();

      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(
          `${API_BASE_URL}/insurance-documents/upload-markdown`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
            timeout: 60000,
            signal: abortControllerRef.current.signal
          }
        );

        uploadResults.push({ 
          file: file.name, 
          success: true, 
          data: response.data 
        });
        successCount++;
      } catch (err) {
        if (axios.isCancel(err) || err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
          uploadResults.push({ 
            file: file.name, 
            success: false, 
            error: 'Cancelled by user' 
          });
          break;
        }
        
        let errorMessage = 'Unknown error';
        if (err.response?.data?.detail) {
          errorMessage = err.response.data.detail;
        } else if (err.message) {
          errorMessage = err.message;
        }
        
        uploadResults.push({ 
          file: file.name, 
          success: false, 
          error: errorMessage 
        });
        errorCount++;
      }
    }

    abortControllerRef.current = null;
    setResults(uploadResults);
    setFiles([]);
    setCancelling(false);
    
    // Reset file input element
    const fileInput = document.getElementById('markdown-upload-multiple');
    if (fileInput) {
      fileInput.value = '';
    }

    if (!cancelledRef.current) {
      setStatus(`Upload complete! ${successCount} succeeded, ${errorCount} failed.`);
    }

    // Notify parent component of successful uploads
    if (onUploadSuccess && successCount > 0) {
      onUploadSuccess();
    }

    setUploading(false);
    setProgress({ current: 0, total: 0 });

    // Auto-hide results after 5 seconds
    setTimeout(() => {
      setResults([]);
      setStatus('');
    }, 5000);
  };

  const getTotalSize = () => {
    const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
    if (totalBytes > 1024 * 1024) {
      return `${(totalBytes / (1024 * 1024)).toFixed(2)} MB`;
    }
    return `${(totalBytes / 1024).toFixed(2)} KB`;
  };

  return (
    <div className="pdf-uploader">
      <div 
        className="upload-header"
        onClick={() => setIsExpanded(!isExpanded)}
        style={{ cursor: 'pointer' }}
      >
        <div className="upload-header-content">
          <div>
            <h2>Upload Markdown Files</h2>
            <p className="upload-subtitle">Upload one or more Markdown files (.md or .markdown)</p>
          </div>
          <div className={`chevron-icon ${isExpanded ? 'expanded' : ''}`}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>
      </div>

      <div className={`upload-container ${isExpanded ? 'expanded' : 'collapsed'}`}>
        <div className="file-input-wrapper">
          <input
            type="file"
            id="markdown-upload-multiple"
            accept=".md,.markdown,text/markdown,text/plain"
            onChange={handleFileChange}
            disabled={uploading}
            className="file-input"
            multiple
          />
          <label htmlFor="markdown-upload-multiple" className="file-label">
            <div className="file-label-content">
              <div className="file-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 13H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 17H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M10 9H9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="file-label-text">
                {files.length > 0 ? (
                  <>
                    <span className="file-name">{files.length} file{files.length > 1 ? 's' : ''} selected</span>
                    <span className="file-size">Total: {getTotalSize()}</span>
                    <div className="selected-files-list">
                      {files.slice(0, 5).map((file, idx) => (
                        <span key={idx} className="selected-file-item">📄 {file.name}</span>
                      ))}
                      {files.length > 5 && (
                        <span className="selected-file-item">...and {files.length - 5} more</span>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <span className="file-label-main">Click to browse or drag and drop</span>
                    <span className="file-label-hint">Select multiple Markdown files (.md, .markdown)</span>
                  </>
                )}
              </div>
              {files.length === 0 && (
                <div className="file-browse-button">
                  Browse Files
                </div>
              )}
            </div>
          </label>
        </div>

        {uploading ? (
          <button
            onClick={handleCancelUpload}
            disabled={cancelling}
            className="upload-button cancel-upload-button"
          >
            {cancelling ? (
              <>
                <span className="button-spinner"></span>
                Cancelling...
              </>
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Cancel Upload
              </>
            )}
          </button>
        ) : (
          <button
            onClick={handleUpload}
            disabled={files.length === 0}
            className="upload-button"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M7 10L12 15L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Upload {files.length > 0 ? `${files.length} File${files.length > 1 ? 's' : ''}` : 'Markdown'}
          </button>
        )}
      </div>

      {/* Progress bar during upload */}
      {uploading && progress.total > 0 && (
        <div className="upload-progress-container">
          <div className="upload-progress-header">
            <span className="upload-progress-text">
              {cancelling 
                ? 'Cancelling...' 
                : `Uploading file ${progress.current} of ${progress.total}`
              }
            </span>
            <span className="upload-progress-percent">
              {Math.round((progress.current / progress.total) * 100)}%
            </span>
          </div>
          <div className="upload-progress-bar">
            <div 
              className={`upload-progress-fill ${cancelling ? 'cancelling' : ''}`}
              style={{ width: `${(progress.current / progress.total) * 100}%` }}
            ></div>
          </div>
        </div>
      )}

      {/* Status messages */}
      {status && !uploading && (
        <div className="status-message">
          <span className="status-icon">📄</span>
          <span>{status}</span>
        </div>
      )}

      {uploading && (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>{status || 'Uploading markdown files...'}</p>
        </div>
      )}

      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          <span>{error}</span>
        </div>
      )}

      {results.length > 0 && (
        <div className="success-container">
          <div className="success-header">
            <span className="success-icon">✅</span>
            <h3>Upload Results ({results.filter(r => r.success).length}/{results.length} succeeded)</h3>
          </div>
          <div className="upload-results-list">
            {results.map((result, idx) => (
              <div key={idx} className={`upload-result-item ${result.success ? 'success' : 'failed'}`}>
                <span className="result-icon">{result.success ? '✓' : '✗'}</span>
                <span className="result-filename">{result.file}</span>
                {result.success ? (
                  <span className="result-id">ID: {result.data.id}</span>
                ) : (
                  <span className="result-error">{result.error}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MarkdownUploader;
